import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchRentals } from "@/lib/api";
import { RENTAL_STATUSES, RENTAL_STATUS_LABELS, RENTAL_STATUS_COLORS, formatDateTime } from "@/lib/constants";
import { MagnifyingGlass, PlusCircle } from "@phosphor-icons/react";

export default function RentalsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") || "";
  const [rentals, setRentals] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Client-side filter when arriving from global search with ?customer=
  const displayedRentals = useMemo(() => {
    if (!customerFilter) return rentals;
    const lower = customerFilter.toLowerCase();
    return rentals.filter(r => r.customer_name?.toLowerCase().includes(lower));
  }, [rentals, customerFilter]);

  useEffect(() => {
    setLoading(true);
    fetchRentals({ status: statusFilter !== "ALL" ? statusFilter : undefined, skip: (page - 1) * limit, limit })
      .then(data => { setRentals(data.items || []); setTotal(data.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter, page]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [statusFilter]);

  return (
    <div className="h-full overflow-y-auto" data-testid="rentals-list-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Rentals</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">{total} total rentals</p>
          </div>
          <button data-testid="rentals-new-btn" onClick={() => navigate("/rentals/new")}
            className="flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors">
            <PlusCircle weight="bold" className="w-5 h-5" /> New Rental
          </button>
        </div>

        {/* Customer filter badge (from global search) */}
        {customerFilter && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 font-medium">Filtered by customer:</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-bold">
              {customerFilter}
              <button
                onClick={() => setSearchParams({})}
                className="ml-1 hover:opacity-70 transition-opacity"
                aria-label="Clear customer filter"
              >
                ✕
              </button>
            </span>
          </div>
        )}

        {/* Status filters */}
        <div className="flex flex-wrap gap-2">
          <button data-testid="filter-all" onClick={() => setStatusFilter("ALL")}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${statusFilter === "ALL" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            All
          </button>
          {RENTAL_STATUSES.map(s => (
            <button key={s} data-testid={`filter-${s.toLowerCase()}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${statusFilter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {RENTAL_STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading...</div>
          ) : rentals.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No rentals found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rentals-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 whitespace-nowrap">
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Rental #</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Vehicle</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Pickup</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Return</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Bond</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayedRentals.map(r => {
                    const sc = RENTAL_STATUS_COLORS[r.status] || {};
                    return (
                      <tr key={r.id} data-testid={`rental-row-${r.rental_number}`}
                        onClick={() => navigate(`/rentals/${r.id}`)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap">
                        <td className="px-4 py-3 font-mono font-bold text-foreground">{r.rental_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-800">{r.customer_name || "—"}</div>
                          <div className="text-xs text-slate-400">{r.customer_mobile}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="text-slate-700">{r.vehicle_display}</div>
                          <div className="text-xs text-slate-400">{r.vehicle_registration}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md border" style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                            {RENTAL_STATUS_LABELS[r.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 hidden lg:table-cell">{formatDateTime(r.pickup_datetime)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 hidden lg:table-cell">{formatDateTime(r.return_datetime)}</td>
                        <td className="px-4 py-3 text-right font-bold text-slate-700">${r.bond_amount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination Controls */}
          {!loading && total > limit && (
            <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-between">
              <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs font-bold rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= total}
                  className="px-3 py-1.5 text-xs font-bold rounded-md bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
